import { Container } from 'unstated';
import { getPersistentStore, getPersistentStoreMutex } from '@lemonadefashion/storage';

// Note, some users will have data stored in the popups_shown key.
const KEY = 'popups_shown_v3';
function _getStore() { return getPersistentStore(); }
function _getMutex() { return getPersistentStoreMutex(); }

function _adapt(info) {
  return {
    name: info.name,
    timestamp: info.timestamp,
    furtherActionNeeded: info.furtherActionNeeded,
    // If the popup info exists, it means we showed it before at least once.
    // Therefore, using 1 is a graceful fallback.
    shownTimes: info.shownTimes || 1
  };
}

// This function is guaranteed not to acquire any locks or do any writes.
function getShownPopupsFromStore() {
  try {
    const popupsJson = _getStore().getItem(KEY);
    if (popupsJson == null) return {};
    const popups = JSON.parse(popupsJson);
    Object.keys(popups).forEach(name => {
      popups[name] = _adapt(popups[name]);
    });
    return popups;
  } catch (error) {
    console.log(error);
    return {};
  }
}

function updatePopupInStore(newPopup) {
  return new Promise((resolve, reject) => {
    const mutex = _getMutex();
    const store = _getStore();
    mutex.lock(KEY)
      .then(() => {
        const elems = getShownPopupsFromStore();
        elems[newPopup.name] = newPopup;
        store.setItem(KEY, JSON.stringify(elems));
        return mutex.release(KEY).then(resolve).catch(reject);
      })
      .catch(reject);
  });
}

class ShownPopupsContainer extends Container {
  constructor() {
    super();
    this.state = {
      popups: getShownPopupsFromStore(),
      userRequestCounter: {}
    };
    window.addEventListener('storage', e => {
      if (e.key !== KEY) return;
      this.setState({ popups: getShownPopupsFromStore() });
    });
  }

  _getPopup(name) {
    const popup = this.state.popups[name];
    if (popup) return popup;
    return {
      name: name,
      timestamp: 0,
      shownTimes: 0,
      furtherActionNeeded: true
    };
  }

  hasUserRequestToShow(name) {
    return (this.state.userRequestCounter[name] || 0) > 0;
  }

  popUserRequestToShow(name) {
    this.setState(prevState => {
      const tempUserRequestCounter = JSON.parse(JSON.stringify(prevState.userRequestCounter));
      tempUserRequestCounter[name] = (tempUserRequestCounter[name] || 0) - 1;
      return { userRequestCounter: tempUserRequestCounter };
    });
  }

  pushUserRequestToShow(name) {
    this.setState(prevState => {
      const tempUserRequestCounter = JSON.parse(JSON.stringify(prevState.userRequestCounter));
      tempUserRequestCounter[name] = (tempUserRequestCounter[name] || 0) + 1;
      return { userRequestCounter: tempUserRequestCounter };
    });
  }

  wasPopupEverShown(name) { return this._getPopup(name).shownTimes > 0; }
  lastPopupShowTime(name) { return this._getPopup(name).timestamp; }
  getPopupShowCount(name) { return this._getPopup(name).shownTimes; }
  popupNeedsFurtherAction(name) { return this._getPopup(name).furtherActionNeeded; }

  recordPopupShow(name, timestamp) {
    return new Promise((resolve, reject) => {
      const newPopup = JSON.parse(JSON.stringify(this._getPopup(name)));
      newPopup.timestamp = timestamp;
      newPopup.shownTimes += 1;
      updatePopupInStore(newPopup)
        .then(() => this.setState({ popups: getShownPopupsFromStore() }, resolve))
        .catch(reject);
    });
  }

  markAsNoFurtherActionNeeded(name) {
    return new Promise((resolve, reject) => {
      const newPopup = JSON.parse(JSON.stringify(this._getPopup(name)));
      newPopup.furtherActionNeeded = false;
      updatePopupInStore(newPopup)
        .then(() => this.setState({ popups: getShownPopupsFromStore() }, resolve))
        .catch(reject);
    });
  }
}

export default ShownPopupsContainer;
